import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ImobiliariaForAutocompleteResponse, RankingItem, RankingPagedResponse } from 'app/shared/models/ei-models';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { DefaultComponentsModule } from 'app/shared/modules/default-components.module';
import { AuthService, GlobalService } from 'app/shared/services/core';
import { ImobiliariaService, RankingService } from 'app/shared/services/edduca-imob';
import { useMenuTracking } from 'app/shared/hooks';

@Component({
  selector: 'app-ranking',
  imports: [DefaultComponentsModule],
  templateUrl: './ranking.component.html',
  styleUrl: './ranking.component.scss'
})
export class RankingComponent implements OnInit {
  private _rankingService = inject(RankingService);
  private _imobiliariaService = inject(ImobiliariaService);
  private _auth = inject(AuthService);
  private _gs = inject(GlobalService);

  // Estado
  loading = signal(false);
  error = signal<string | null>(null);
  data = signal<RankingPagedResponse | null>(null);

  // Paginação
  page = signal(1);
  pageSize = signal(10);
  
  // IDs
  currentUserId: number | null = null;
  currentImobiliariaId: number | null = null;
  currentPerfilId: number | null = null;

  // Top 3 e lista
  top3: RankingItem[] = [];
  classificacao: RankingItem[] = [];

  // Autocomplete de imobiliárias
  imobiliariaCtrl = new FormControl<ImobiliariaForAutocompleteResponse | null>(null);
  imobiliariaOptions: ImobiliariaForAutocompleteResponse[] = [];
  filteredImobiliarias: ImobiliariaForAutocompleteResponse[] = [];

  // Computed
  totalRegistros = computed(() => this.data()?.totalRegistros ?? 0);
  totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()))
  );
  hasPrev = computed(() => this.page() > 1);
  hasNext = computed(() => this.page() < this.totalPages());
  showPagination = computed(() => this.totalRegistros() > this.pageSize());
  
  private menuTracking = useMenuTracking();

  /**
   * Verifica se o usuário precisa selecionar uma imobiliária
   */
  get needsImobiliariaSelection(): boolean {
    return this.currentPerfilId === PerfilEnum.ADM_SISTEMA || 
           this.currentPerfilId === PerfilEnum.PARCEIRO;
  }

  /**
   * Nome da imobiliária selecionada (para exibição no header)
   */
  get selectedImobiliariaName(): string | null {
    return this.imobiliariaCtrl.value?.nome || null;
  }

  ngOnInit(): void {
    // Registrar acesso ao menu ranking para gamificação
    this.menuTracking.registrarAcessoMenu('ranking');
    
    // Carrega dados do token
    const userInfo = this._auth.getUserInToken();
    this.currentUserId = userInfo?.userId || null;
    this.currentPerfilId = userInfo?.perfilId || null;
    this.currentImobiliariaId = userInfo?.imobiliariaId || null;

    if (this.needsImobiliariaSelection) {
      // ADM_SISTEMA ou PARCEIRO: carrega lista de imobiliárias para o autocomplete
      this.loadImobiliarias();
    } else if (this.currentImobiliariaId) {
      // ADM_IMOBILIARIA ou USUARIO_IMOBILIARIA: carrega ranking direto
      this.loadRanking();
    } else {
      this.error.set('Não foi possível identificar a imobiliária do usuário.');
    }
  }

  /**
   * Carrega lista de imobiliárias para o autocomplete
   */
  loadImobiliarias(): void {
    this._imobiliariaService.getAllForAutocomplete().subscribe({
      next: (imobiliarias) => {
        this.imobiliariaOptions = imobiliarias;
        this.filteredImobiliarias = imobiliarias;
      },
      error: () => {
        this.error.set('Falha ao carregar lista de imobiliárias');
      }
    });
  }

  /**
   * Filtra imobiliárias no autocomplete
   */
  filterImobiliarias(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredImobiliarias = this.imobiliariaOptions.filter(imob => 
      imob.nome.toLowerCase().includes(value)
    );
  }

  /**
   * Exibe nome da imobiliária no input
   */
  displayImobiliariaFn(imobiliaria: ImobiliariaForAutocompleteResponse): string {
    return imobiliaria?.nome || '';
  }

  /**
   * Callback quando uma imobiliária é selecionada
   */
  onImobiliariaSelected(imobiliaria: ImobiliariaForAutocompleteResponse): void {
    this.currentImobiliariaId = imobiliaria.id;
    this.page.set(1);
    this.loadRanking();
  }

  /**
   * Carrega o ranking da imobiliária
   */
  loadRanking(): void {
    if (!this.currentImobiliariaId) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this._rankingService.listar(
      this.currentImobiliariaId,
      this.currentUserId || undefined,
      this.page(),
      this.pageSize()
    ).subscribe({
      next: (res) => {
        this.data.set(res);

        const itens = res.itens ?? [];
        this.top3 = itens.slice(0, 3);
        this.classificacao = itens;

        this.loading.set(false);
      },
      error: () => {
        this.error.set('Falha ao carregar ranking');
        this.loading.set(false);
      }
    });
  }

  irAnterior(): void {
    if (this.hasPrev()) {
      this.page.set(this.page() - 1);
      this.loadRanking();
    }
  }

  irProxima(): void {
    if (this.hasNext()) {
      this.page.set(this.page() + 1);
      this.loadRanking();
    }
  }

  /**
   * Retorna as classes CSS para a badge de posição
   */
  getPositionClasses(posicao: number): string {
    if (posicao === 1) {
      return 'bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow-lg shadow-amber-500/30';
    }
    if (posicao === 2) {
      return 'bg-gradient-to-br from-gray-300 to-gray-500 text-zinc-900 shadow-lg';
    }
    if (posicao === 3) {
      return 'bg-gradient-to-br from-amber-600 to-amber-800 text-white shadow-lg';
    }
    return 'bg-zinc-800 text-white/60 border border-zinc-700';
  }

  trackByUser = (_: number, item: RankingItem) => item.usuarioId;
}